/* Atlas v3 — the Insights reader (the /insights/:id detail page).

   Replaces the old V2 InsightDetail (violet card + old AppLayout nav). A
   type-forward editorial reader on true black matching "The Weekly"
   magazine: Instrument Serif headline, JetBrains-mono labels/eyebrows,
   amber as line/label only (the mockup's violet is flattened to amber per
   the v3 rules), green/red for direction only, hairline-divided stat rows
   instead of a bordered card.

   Brings its own shell (TickerStrip + BottomNav) like the magazine — NOT
   wrapped in the old AppLayout. Ticker chips → openTicker(symbol) via the
   shared StockDetail drawer. Data ← useInsightDetail(id) (/api/insights/:id,
   carries body_md). */
import { TickerStrip, BottomNav, SearchPill } from './shell'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { useV2Path, v2Navigate } from '@/v2/lib/useV2Path'
import { useInsightDetail } from '@/v2/hooks/useInsightDetail'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Serif = { fontFamily: 'var(--studio-font-display)', fontStyle: 'normal', fontWeight: 400 }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const HAIR = '1px solid hsl(var(--studio-border) / 0.7)'

function statColor(cls) {
  if (cls === 'up') return '--studio-success'
  if (cls === 'dn' || cls === 'down') return '--studio-danger'
  return '--studio-text'
}
function timeAgo(iso) {
  if (!iso) return ''
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (Number.isNaN(d)) return ''
  if (d >= 7) return `${Math.floor(d / 7)}w ago`
  if (d >= 1) return `${d}d ago`
  return 'today'
}

function Article({ insight, openTicker }) {
  const stats = insight.extracted_stats || []
  const tickers = insight.tickers || []
  const body = insight.body_md || ''
  const paragraphs = body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)

  return (
    <div style={{ padding: '12px 24px 40px' }}>
      {/* Eyebrow — sector label only */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'hsl(var(--studio-accent))' }}>{insight.sector_primary}</span>
        {insight.sector_secondary && (
          <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'hsl(var(--studio-text-subtle))' }}>· {insight.sector_secondary}</span>
        )}
      </div>

      {/* Headline — Instrument Serif */}
      <h1 style={{ ...Serif, fontSize: 31, lineHeight: 1.16, letterSpacing: '-0.01em', color: 'hsl(var(--studio-text))', margin: 0, textWrap: 'pretty' }}>{insight.headline}</h1>

      {/* Snippet — lead paragraph */}
      {insight.snippet && (
        <p style={{ ...Sans, fontSize: 14, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', margin: '16px 0 0', textWrap: 'pretty' }}>{insight.snippet}</p>
      )}

      {/* Stat rows — hairline-divided, NOT a bordered card */}
      {stats.length > 0 && (
        <div style={{ marginTop: 24, borderTop: HAIR }}>
          {stats.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 14, padding: '12px 0', borderBottom: i === stats.length - 1 ? 'none' : HAIR }}>
              <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'hsl(var(--studio-text-subtle))' }}>{s.label}</span>
              <span style={{ ...Mono, fontSize: 14, fontVariantNumeric: 'tabular-nums', textAlign: 'right', color: `hsl(var(${statColor(s.color_class)}))` }}>{s.value_text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Body */}
      {paragraphs.length > 0 ? (
        <div style={{ marginTop: 26 }}>
          {paragraphs.map((p, i) => (
            <p key={i} style={{ ...Sans, fontSize: 14, lineHeight: 1.72, color: 'hsl(var(--studio-text-muted))', margin: i === 0 ? 0 : '16px 0 0', textWrap: 'pretty' }}>{p}</p>
          ))}
        </div>
      ) : (
        <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginTop: 26, padding: '12px 14px', border: '1px dashed hsl(var(--studio-border))', borderRadius: 6 }}>
          FULL ANALYSIS GENERATING — REFRESH SHORTLY
        </div>
      )}

      {/* Tickers → open the drawer */}
      {tickers.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.18em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 9 }}>TICKERS</div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {tickers.map((t) => (
              <button key={t.id || t.ticker} onClick={() => openTicker(t.ticker)} style={{
                ...Mono, fontSize: 11.5, letterSpacing: '0.04em', cursor: 'pointer',
                color: 'hsl(var(--studio-text))', background: 'hsl(var(--studio-surface) / 0.55)',
                border: '1px solid hsl(var(--studio-border) / 0.6)', borderRadius: 999, padding: '6px 13px',
              }}>{t.ticker}{t.is_lead ? ' ·' : ''}</button>
            ))}
          </div>
        </div>
      )}

      {/* Source row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 30, paddingTop: 16, borderTop: HAIR, ...Mono, fontSize: 9.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))' }}>
        <span>{insight.source_name ? `FROM ${insight.source_name}` : 'ATLAS RESEARCH'}{insight.source_date ? ` · ${insight.source_date}` : ''}</span>
        <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span>{timeAgo(insight.source_date || insight.created_at)}</span>
          {insight.source_url && (
            <a href={insight.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'hsl(var(--studio-accent))', textDecoration: 'none', borderLeft: '1px solid hsl(var(--studio-accent) / 0.3)', paddingLeft: 10 }}>SOURCE ↗</a>
          )}
        </span>
      </div>
    </div>
  )
}

function ReaderNote({ tone = 'subtle', kicker, children }) {
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 32px', textAlign: 'center' }}>
      <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.22em', color: tone === 'error' ? 'hsl(var(--studio-danger))' : 'hsl(var(--studio-accent))', marginBottom: 14 }}>{kicker}</div>
      <div style={{ ...Sans, fontSize: 13, lineHeight: 1.6, color: 'hsl(var(--studio-text-subtle))', maxWidth: 260 }}>{children}</div>
    </div>
  )
}

function InsightReaderBody() {
  const { openTicker } = useStockDetail()
  const path = useV2Path()
  const m = path.match(/insights\/([^/?#]+)/)
  const id = m ? decodeURIComponent(m[1]) : null
  const { insight, loading, error } = useInsightDetail(id)

  return (
    <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
      background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
      {/* back affordance */}
      <div style={{ flexShrink: 0, padding: '18px 24px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <button onClick={() => v2Navigate('/insights')} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          ...Mono, fontSize: 10, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>← INSIGHTS</button>
        <SearchPill />
      </div>

      <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {loading && <ReaderNote kicker="THE ATLAS WEEKLY">Loading this story&hellip;</ReaderNote>}
        {!loading && (error || !insight) && (
          <ReaderNote tone="error" kicker="INSIGHT NOT FOUND">
            {error === 'not_found' ? 'This insight may have been archived or removed.' : "Couldn't load this insight — try refreshing."}
          </ReaderNote>
        )}
        {!loading && !error && insight && <Article insight={insight} openTicker={openTicker} />}
      </div>

      <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
        <TickerStrip />
      </div>
      <BottomNav />
    </div>
  )
}

export function InsightReader() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <InsightReaderBody />
      </StockDetailProvider>
    </div>
  )
}
